import { useSyncExternalStore } from 'react'

/** Read-aloud voice preference — device-local, like the theme choice. */
export type TtsVoicePreference = 'female' | 'male'

export const TTS_VOICE_STORAGE_KEY = 'hub-tts-voice'

/**
 * The one source of truth is this module (localStorage + an in-memory copy for
 * when storage is unavailable); every UI that shows the choice (today the
 * settings dialog's Profil section) reads it through `useTtsVoice`, so all of
 * them — and read-aloud via `getTtsVoice` — always agree.
 */
let sessionVoice: TtsVoicePreference | null = null
const listeners = new Set<() => void>()

export function getTtsVoice(): TtsVoicePreference {
  if (sessionVoice) return sessionVoice
  try {
    const raw = window.localStorage.getItem(TTS_VOICE_STORAGE_KEY)
    return raw === 'male' ? 'male' : 'female'
  } catch {
    return 'female'
  }
}

export function setTtsVoice(voice: TtsVoicePreference): void {
  try {
    window.localStorage.setItem(TTS_VOICE_STORAGE_KEY, voice)
    sessionVoice = null
  } catch {
    // Storage unavailable: keep the choice in memory for this session.
    sessionVoice = voice
  }
  for (const listener of listeners) listener()
}

export function subscribeTtsVoice(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The current voice + its setter, re-rendering on every change. */
export function useTtsVoice(): [TtsVoicePreference, (voice: TtsVoicePreference) => void] {
  const voice = useSyncExternalStore(subscribeTtsVoice, getTtsVoice, () => 'female' as const)
  return [voice, setTtsVoice]
}
